<template>
  <div>
    <Head title="Paid PRs" />
    <h1 class="mb-8 text-3xl font-bold">Prepaid PRs</h1>
    <div class="mb-3">
      <Link href="/payment-requests" class="text-orange-600 hover:text-orange-500 mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-6 h-6 inline"
          style="display: inline !important"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
          />
        </svg>
        Back
      </Link>
    </div>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block text-gray-700">Role:</label>
        <select class="form-select mt-1 w-full">
          <option :value="null" />
          <option />
        </select>
      </search-filter>
      <a
        v-if="
          user.group_permissions.includes('create_payment_requisition') ||
          user.per_user_permissions.includes('create_payment_requisition')
        "
        class="btn-orange"
        href="#"
        @click="openCreateNewPrWindow"
      >
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;New</span>
      </a>
    </div>
    <hr />

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-2">Date Created</th>
          <th class="pb-3 pt-3 px-2">PR Number</th>
          <th class="pb-3 pt-3 px-2">Supplier Name</th>
          <th class="pb-3 pt-3 px-2 text-end">Amount</th>
          <th class="pb-3 pt-3 px-2">Requested By</th>
          <th class="pb-3 pt-3 px-2">Department</th>
          <th class="pb-3 pt-3 px-2">Date Paid</th>
          <th class="pb-3 pt-3 px-2" colspan="2">Status</th>
        </tr>
        <tr
          v-for="pr in payment_requests.data"
          :key="pr.id"
          class="hover:bg-orange-100 focus-within:bg-gray-100"
        >
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2 focus:text-indigo-500"
              :href="`/payment-requests/${pr.id}/payment-request-details`"
            >
              {{ pr.date }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.pr_number }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.supplier }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center justify-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.total }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.user }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.department }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.date_paid }}
            </a>
          </td>
          <td class="border-t">
            <a
              class="flex items-center px-2 py-2"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              {{ pr.status }}
            </a>
          </td>
          <td class="w-px border-t">
            <a
              class="flex items-center px-4"
              :href="`/payment-request/${pr.id}/payment-request-details`"
              tabindex="-1"
            >
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </a>
          </td>
        </tr>
        <tr v-if="payment_requests.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="8">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="payment_requests.links" />
  </div>
  <div v-if="isCreatePrOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 24px">New Payment Request</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              You are about to create new Payment Request, do you wish to continue?
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
                @click="openCreateNewPrWindow"
              >
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5"
                @click="closeOpenPR"
              >
                No
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="isCreatePrWindowOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left w-1/2 overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 24px">New Payment Request</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="flex flex-wrap -mb-8 -mr-6 p-8">
              <div class="pb-8 pr-6 w w-full lg:w-full">
                <p class="mb-2">Supplier:</p>
                <multiselect
                  id="supplier"
                  v-model="selectedSupplier"
                  placeholder="Search Supplier"
                  :options="suppliers"
                  :close-on-select="true"
                  :searchable="true"
                  @search-change="onSearchSuppliersChange"
                  @input="onSelectedSupplier"
                />
              </div>
              <div class="pb-8 pr-6 w w-full lg:w-full">
                <p class="mb-2">Department:</p>
                <multiselect
                  id="department"
                  v-model="selectedDepartment"
                  placeholder="Search Department"
                  :options="departments"
                  :close-on-select="true"
                  :searchable="true"
                  @search-change="onSearchDepartmentsChange"
                  @input="onSelectedDepartment"
                />
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button
                v-if="
                  user.group_permissions.includes('create_payment_requisition') ||
                  user.per_user_permissions.includes('create_payment_requisition') ||
                  user.per_user_permissions.includes('create_global_payment_requisition')
                "
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
                @click="submitNewPR"
              >
                Save
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button
                type="button"
                class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5"
                @click="closeOpenPR"
              >
                Cancel
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Icon from "@/Shared/Icon";
import pickBy from "lodash/pickBy";
import Layout from "@/Shared/Layout";
import throttle from "lodash/throttle";
import mapValues from "lodash/mapValues";
import SearchFilter from "@/Shared/SearchFilter";
import Pagination from "@/Shared/Pagination";
import Multiselect from "@vueform/multiselect";

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    Multiselect,
  },
  layout: Layout,
  props: {
    filters: Object,
    payment_requests: Object,
    suppliers: Object,
    departments: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      formData: {
        supplier: null,
        department: null,
      },
      rolePermissions: {
        viewUsers: "view users",
      },
      isCreatePrOpen: false,
      isCreatePrWindowOpen: false,
    };
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user;
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get("/prepaid-payment-request", pickBy(this.form), {
          preserveState: true,
        });
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    openCreateNewPr() {
      this.isCreatePrOpen = true;
    },
    closeOpenPR() {
      this.isCreatePrOpen = false;
      this.isCreatePrWindowOpen = false;
    },
    openCreateNewPrWindow() {
      this.isCreatePrWindowOpen = true;
    },
    submitNewPR() {
      const prInt = 0;
      this.$inertia.post("/payment-requests", this.formData, {
        onSuccess: () => {
          this.isCreatePrOpen = false;
          this.$router.push(`/payment-request/${prInt}/payment-request-details`);
        },
      });
    },
    onSelectedSupplier(supplier, id) {
      this.formData.supplier = supplier;
    },
    onSelectedDepartment(department, id) {
      this.formData.department = department;
    },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
.multiselect-option.is-selected.is-pointed {
  background: var(--ms-option-bg-selected-pointed, #fb923c);
  color: var(--ms-option-color-selected-pointed, #fff);
}
.multiselect-option.is-selected {
  background: var(--ms-option-bg-selected-pointed, #fb923c);
  color: var(--ms-option-color-selected-pointed, #fff);
}
</style>
